import styled, { css } from 'styled-components';
import { getSpacingToken, getTextTokenPreset, getToken } from '@optimus-web/common';
import { ListItemVariant } from './types';

const LIST_HORIZONTAL_PADDING = 16;

export interface ListItemContainerElementProps {
    dense?: boolean;
    inactive?: boolean;
    indent?: boolean;
    twoLine?: boolean;
    threeLine?: boolean;
    selected?: boolean;
    active?: boolean;
    focused?: boolean;
    singleLineWithAvatar?: boolean;
    wrapText?: boolean;
    depth?: number;
    disabled?: boolean;
    keyboardHandled?: boolean;
}

const getListItemHeight = (props: ListItemContainerElementProps) => (props.dense ? 40 : 48);
const getListVerticalPadding = (props: ListItemContainerElementProps) => (props.dense ? '4px' : '8px');

export interface PrimaryTextElementProps {
    dense?: boolean;
    wrapText?: boolean;
    variant?: ListItemVariant;
}

export const PrimaryTextElement = styled.span<PrimaryTextElementProps>`
    color: ${(props) => (props.variant === 'error' ? getToken('textAlertDanger') : getToken('textStaticPrimary'))};
    ${(props) => getTextTokenPreset(props.dense ? 'bodyMedium' : 'bodyLarge')}
`;

export const SecondaryTextElement = styled.span`
    color: ${getToken('textStaticSecondary')};
    ${getTextTokenPreset('bodyMedium')};
`;

export const ListItemContainerElement = styled.div<ListItemContainerElementProps>`
    align-items: center;
    box-sizing: border-box;
    display: flex;
    flex-shrink: 0;
    outline: none;
    padding: ${(props) => getListVerticalPadding(props)} ${LIST_HORIZONTAL_PADDING}px;
    position: relative;
    min-height: ${getListItemHeight}px;
    text-decoration: none;

    ${({ dense, singleLineWithAvatar }) =>
        singleLineWithAvatar &&
        css`
            min-height: ${dense ? '48px' : '56px'};
        `}

    ${({ dense, twoLine }) =>
        twoLine &&
        css`
            min-height: ${dense ? '60px' : '72px'};
        `}

    ${({ dense, threeLine }) =>
        threeLine &&
        css`
            min-height: ${dense ? '76px' : '88px'};
        `}

    ${({ hidden }) =>
        hidden &&
        css`
            display: none;
        `}

    ${(props) =>
        props.active &&
        css`
            background-color: ${getToken('backgroundInteractiveNeutralSubtleActive')};
        `}

    ${(props) =>
        props.focused &&
        css`
            background-color: ${getToken('backgroundInteractiveNeutralSubtleHover')};
        `}

    ${(props) =>
        props.selected &&
        css`
            background-color: ${getToken('backgroundInteractiveNeutralActive')};
        `}

    ${({ disabled }) =>
        disabled &&
        css`
            opacity: ${getToken('opacity400')};
            cursor: default;

            > * {
                pointer-events: none;
            }
        `}

    &:focus {
        ${({ disabled }) =>
            !disabled &&
            css`
                background-color: ${getToken('backgroundInteractiveNeutralSubtleHover')};
            `}
    }

    &:hover {
        ${({ disabled, keyboardHandled }) =>
            !keyboardHandled &&
            !disabled &&
            css`
                background-color: ${getToken('backgroundInteractiveNeutralSubtleHover')};
                cursor: pointer;
            `}
    }

    padding-left: ${({ depth, theme }) => LIST_HORIZONTAL_PADDING + (depth || 0) * getSpacingToken('200')({ theme })}px;

    ${SecondaryTextElement} {
        color: ${({ inactive }) => getToken(inactive ? 'textDisabled' : 'textStaticSecondary')};
    }

    ${({ indent }) =>
        indent &&
        css`
            padding-left: 32px;
        `}
`;

export const LeftContentElement = styled.span`
    align-items: center;
    display: flex;
`;

export const RightContentElement = styled.span`
    align-items: center;
    display: flex;
    margin-left: auto;
    padding-left: ${LIST_HORIZONTAL_PADDING}px;
`;

export interface IconElementProps {
    left?: boolean;
    right?: boolean;
}
export const IconElement = styled.span<IconElementProps>`
    display: flex;
    color: ${getToken('textStaticSecondary')};

    ${({ left }) =>
        left &&
        css`
            margin-right: ${getSpacingToken('400')}px;
        `}
    ${({ right }) =>
        right &&
        css`
            margin-left: ${getSpacingToken('200')}px;
        `}
`;

export const AvatarElement = styled.span`
    display: flex;
    justify-content: center;
    margin-right: ${getSpacingToken('200')}px;
`;

export const ContentElement = styled.span`
    display: flex;
    flex-flow: column;
    width: 100%;
`;

interface CheckboxWrapperProps {
    left?: boolean;
}
export const CheckboxWrapper = styled.div<CheckboxWrapperProps>`
    ${({ left }) =>
        left &&
        css`
            margin-right: ${getSpacingToken('200')}px;
        `}
`;
